/**
 * plyxChime — play the Plyx brand chime ("the Plyx ding") at micro-
 * moments throughout the app to build audio-brand recognition.
 *
 * Asset: `/sounds/plyx-chime.mp3` (~11 KB, 0.9s, two-note ascending).
 *
 * Behaviour:
 *   • One shared HTMLAudioElement per app session — avoids GC churn
 *     from spinning up Audio objects on every call.
 *   • Respects user mute preference via `localStorage["plyx-chime"]`
 *     (set to "off" to silence everywhere).
 *   • Auto-throttled to one play per `MIN_GAP_MS` (default 1.2s) so
 *     rapid-fire actions don't stack chimes on top of each other.
 *   • Honours OS `prefers-reduced-motion` (a proxy for "I want a quiet
 *     experience") by default — users who opt out of motion are
 *     probably also opting out of surprise sound.
 *   • Lazy-loads: the <audio> element is created on first play(), not
 *     at module import, so SSR/build-time evaluation stays cheap.
 */

const ASSET_URL = "/sounds/plyx-chime.mp3";
const STORAGE_KEY = "plyx-chime";
const MIN_GAP_MS = 1200;

let _audio = null;
let _lastPlayAt = 0;
let _ctx = null; // WebAudio for in-app loud-moment fallback

const isMutedByUser = () => {
  try { return localStorage.getItem(STORAGE_KEY) === "off"; }
  catch { return false; }
};

const isReducedMotion = () => {
  try { return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches; }
  catch { return false; }
};

const ensureAudio = () => {
  if (_audio) return _audio;
  _audio = new Audio(ASSET_URL);
  _audio.preload = "auto";
  _audio.volume = 0.6;
  return _audio;
};

/** Play the chime. Returns a Promise<boolean> — true if sound actually
 *  started, false if suppressed by mute / throttle / autoplay block.
 *
 *  @param {Object} [opts]
 *  @param {number} [opts.volume=0.6]  0..1
 *  @param {boolean} [opts.force=false] bypass mute + reduced-motion
 *                                     (use sparingly — only for explicit
 *                                     user-initiated celebration moments).
 */
export const playPlyxChime = async (opts = {}) => {
  const { volume = 0.6, force = false } = opts;
  if (!force) {
    if (isMutedByUser() || isReducedMotion()) return false;
  }
  const now = Date.now();
  if (now - _lastPlayAt < MIN_GAP_MS) return false;
  _lastPlayAt = now;
  try {
    const audio = ensureAudio();
    audio.volume = Math.max(0, Math.min(1, volume));
    audio.currentTime = 0;
    await audio.play();
    return true;
  } catch {
    // Browsers may reject if the user hasn't interacted yet. Silent
    // fail — the chime is decorative, never required.
    return false;
  }
};

/** Convenience to toggle mute persistently. */
export const setPlyxChimeMuted = (muted) => {
  try {
    if (muted) localStorage.setItem(STORAGE_KEY, "off");
    else localStorage.removeItem(STORAGE_KEY);
  } catch { /* ignore */ }
};

export const isPlyxChimeMuted = () => isMutedByUser();

export default playPlyxChime;
